import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { P } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { AutoGrid } from '../../payroll/components/common';
import { api } from '../../../lib/api';
import { useMut } from '../../../lib/query';
import { Badge, Button, Card, EmptyState, Icon, IconButton, Loading, Modal, PageHeader, Row, Screen, Stack, Switch, Text, TextField, useTheme } from '../../../ui';

type Integration = Awaited<ReturnType<typeof api.admin.integrations>>[number];
const MASK = '••••••';
const isSecret = (k: string) => /secret|token|password|key/i.test(k);
const toText = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : JSON.stringify(v));

export function IntegrationsScreen() {
  const { colors } = useTheme();
  const { can } = useAuth();
  const edit = can(P.TENANT_MANAGE);
  const q = useQuery({ queryKey: ['integrations'], queryFn: () => api.admin.integrations() });
  const [sel, setSel] = useState<Integration | null>(null);
  const [cfg, setCfg] = useState<[string, string][]>([]);
  const [enabled, setEnabled] = useState(false);
  const [newKey, setNewKey] = useState('');
  const save = useMut(() => api.admin.saveIntegration(sel!.key, { enabled, config: Object.fromEntries(cfg.filter(([k]) => k.trim())) }), {
    invalidate: [['integrations']],
    success: 'Integration saved',
    onSuccess: () => setSel(null),
  });
  const quickToggle = useMut((i: Integration) => api.admin.saveIntegration(i.key, { enabled: !i.enabled, config: i.config ?? {} }), {
    invalidate: [['integrations']],
    success: (_r, i) => `${i.name} ${i.enabled ? 'disabled' : 'enabled'}`,
  });
  const open = (i: Integration) => {
    setSel(i);
    setEnabled(i.enabled);
    setNewKey('');
    setCfg(Object.entries(i.config ?? {}).map(([k, v]) => [k, toText(v)]));
  };
  const setVal = (idx: number, v: string) => setCfg(cfg.map((x, j) => (j === idx ? [x[0], v] : x)));

  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()} testID="integrations">
      <PageHeader title="Integrations" subtitle="Connect SSO, email/WhatsApp notifications, accounting, biometric sync and more." />
      {q.isLoading ? (
        <Loading />
      ) : !q.data?.length ? (
        <EmptyState icon="plug" title="No integrations available" />
      ) : (
        <AutoGrid min={280}>
          {q.data.map((i) => (
            <Card key={i.key} testID={`integration-${i.key}`}>
              <Stack gap={12}>
                <Row justify="space-between" align="flex-start" wrap={false}>
                  <Row gap={10} wrap={false} style={{ flex: 1 }}>
                    <View style={{ width: 36, height: 36, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.surface2 }}>
                      <Icon name="plug" color={colors.muted} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text weight="semibold">{i.name}</Text>
                      <Text variant="small">{i.key}</Text>
                    </View>
                  </Row>
                  <Badge tone={i.enabled ? 'success' : 'neutral'}>{i.enabled ? 'Connected' : 'Off'}</Badge>
                </Row>
                <Text variant="small">{`${Object.keys(i.config ?? {}).length} setting(s) configured`}</Text>
                <Row justify="space-between">
                  <Button icon="settings" title="Configure" onPress={() => open(i)} disabled={!edit} />
                  {edit && (
                    <View style={{ minWidth: 110 }}>
                      <Switch label="Enabled" value={i.enabled} disabled={quickToggle.isPending} onChange={() => quickToggle.mutate(i)} />
                    </View>
                  )}
                </Row>
              </Stack>
            </Card>
          ))}
        </AutoGrid>
      )}
      <Modal
        open={!!sel}
        onClose={() => setSel(null)}
        title={sel?.name ?? ''}
        subtitle={sel?.key}
        width={560}
        testID="integration-form"
        footer={
          <>
            <Button title="Cancel" onPress={() => setSel(null)} />
            <Button variant="primary" title="Save" loading={save.isPending} onPress={() => save.mutate(undefined)} testID="save-integration" />
          </>
        }
      >
        <Switch label="Enabled" value={enabled} onChange={setEnabled} />
        <Text variant="label">Configuration</Text>
        {!cfg.length && <Text variant="muted">No settings yet. Add keys such as client_id, api_key or webhook_url.</Text>}
        {cfg.map(([k, v], idx) => {
          const secret = isSecret(k);
          const masked = secret && v === MASK;
          return (
            <Row key={`${k}-${idx}`} gap={8} align="flex-end" wrap={false}>
              <TextField
                label={k}
                value={masked ? '' : v}
                placeholder={masked ? 'Stored — type to replace' : undefined}
                secureTextEntry={secret}
                autoCapitalize="none"
                autoComplete="off"
                multiline={!secret && v.length > 80}
                hint={masked ? 'Saved secrets stay hidden. Leave blank to keep the current value.' : undefined}
                onChangeText={(t) => setVal(idx, t === '' && secret && sel?.config?.[k] === MASK ? MASK : t)}
                containerStyle={{ flex: 1 }}
              />
              <IconButton icon="trash" label={`Remove ${k}`} onPress={() => setCfg(cfg.filter((_, j) => j !== idx))} />
            </Row>
          );
        })}
        <Row gap={8} wrap={false}>
          <TextField placeholder="Add config key…" autoCapitalize="none" value={newKey} onChangeText={(t) => setNewKey(t.replace(/\s/g, '_'))} accessibilityLabel="New config key" containerStyle={{ flex: 1 }} />
          <Button
            icon="plus"
            title="Add"
            disabled={!newKey || cfg.some(([k]) => k === newKey)}
            onPress={() => {
              setCfg([...cfg, [newKey, '']]);
              setNewKey('');
            }}
          />
        </Row>
      </Modal>
    </Screen>
  );
}
